/**
 * New ▾'s sheets — `docs/activity.md` §9: a text, an email, a task, a
 * meeting, a note — and on a phone the **+**'s *New* sheet (Board 16 D) and
 * the voice note (§11.2, the stop page's recorder).
 *
 * `activity-page.tsx` renders this with the kind asked for and what to start
 * from (§9.2 — a thread, a stop, a free slot, a list). Picking a row on the
 * phone's sheet swaps it for that kind's sheet; *New request* is folder 48's.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { usePhone } from '../../lib/use-narrow.js'
import { openNewRequest } from '../../requests/open-request.js'
import { Glyph } from '../glyphs.js'
import { useActivity } from '../activity-context.js'
import { useBackLayer } from '../use-activity-history.js'
import { VoiceNoteSheet, type VoiceLink } from '../stop/voice-note.js'
import { openTaskSheet } from '../tasks/task-sheet.js'
import { readPrefill, type NewPrefill } from './common.js'
import { EmailSheet } from './email-sheet.js'
import { KindTile, type TileLook } from './kit.js'
import { MeetingSheet } from './meeting-sheet.js'
import { NoteSheet } from './note-sheet.js'
import { TaskSheet } from './task-sheet.js'
import { TextSheet } from './text-sheet.js'

export type NewSheetKind = 'menu' | 'text' | 'email' | 'task' | 'meeting' | 'note' | 'voice'

export interface NewSheetsProps {
  kind: NewSheetKind | null
  /** What to start from — a stop, a thread, free time (§9.2). */
  prefill?: Record<string, unknown> | null
  onClose: () => void
  /** No Inbox, no Text or Email (§19 B18, T2). */
  canText?: boolean
  /** May take a request — folder 48's row. */
  canRequest?: boolean
}

/** Something was made: the calendar, the lists and the counts reload. */
export const ACTIVITY_MADE_EVENT = 'rowted:activity-made'

/** Board 16 D's rows, exactly (§3: the phone's *New* sheet). */
const MENU_ROWS: { kind: Exclude<NewSheetKind, 'menu'> | 'request'; look: TileLook; title: string; sub: string }[] = [
  { kind: 'text', look: 'text', title: 'Text', sub: 'to a contact, attached to their records' },
  { kind: 'email', look: 'email', title: 'Email', sub: 'from you, replies come back here' },
  { kind: 'task', look: 'task', title: 'Task', sub: 'with a due date, for one person or several' },
  { kind: 'meeting', look: 'meeting', title: 'Meeting', sub: 'with a customer, or just the team' },
  { kind: 'note', look: 'note', title: 'Note', sub: 'typed or dictated' },
  { kind: 'voice', look: 'voice', title: 'Voice note', sub: 'on a job, typed for you' },
  { kind: 'request', look: 'request', title: 'New request', sub: 'contact, property, job and a site visit' },
]

/** The **+**'s bottom sheet (Board 16 D): 60 px rows, 38 px tiles, *New* · *for <name>*. */
function NewMenu({
  onPick, onClose, canText, canRequest, forName,
}: {
  onPick: (kind: Exclude<NewSheetKind, 'menu'> | 'request') => void
  onClose: () => void
  canText: boolean
  canRequest: boolean
  forName: string
}) {
  /* Back closes it; a row picked swaps it for that sheet in the same entry (Aaron, Oct 26). */
  useBackLayer(onClose)
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.stopPropagation(); onClose() } }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  const rows = MENU_ROWS.filter((row) => (canText || (row.kind !== 'text' && row.kind !== 'email'))
    && (canRequest || row.kind !== 'request'))
  return createPortal(
    <div data-overlay-layer={60} style={{ zIndex: 60 }} className="fixed inset-0 flex flex-col justify-end bg-[rgba(29,29,26,.38)]"
      onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
      <div role="dialog" aria-modal="true" aria-label="New"
        className="flex max-h-[88dvh] flex-col overflow-hidden rounded-t-sheet-wide bg-surface shadow-[0_-10px_30px_rgba(20,20,10,.12)]">
        <div className="flex justify-center pb-0.5 pt-2"><i className="block h-1 w-10 rounded-full bg-line-strong" /></div>
        <div className="flex items-center gap-3 border-b border-line-faint px-4 pb-3 pt-2.5">
          <div className="flex min-w-0 flex-col">
            <b className="text-[15.5px]">New</b>
            <span className="truncate text-[12px] text-text-muted">for {forName}</span>
          </div>
          <span className="flex-1" />
          <button type="button" aria-label="Close" onClick={onClose}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-line text-text-muted">
            <Glyph name="close" size={16} />
          </button>
        </div>
        <div role="menu" aria-label="New" className="min-h-0 overflow-y-auto px-4 pb-[calc(14px+env(safe-area-inset-bottom))] pt-3.5">
          {rows.map((row, index) => (
            <button key={row.kind} type="button" role="menuitem" onClick={() => onPick(row.kind)}
              className={`flex h-[60px] w-full items-center gap-3 text-left ${index < rows.length - 1 ? 'border-b border-line-faint' : ''}`}>
              <KindTile look={row.look} size={38} />
              <span className="flex min-w-0 flex-col">
                <b className="text-[15px]">{row.title}</b>
                <span className="truncate text-[12px] text-text-muted">{row.sub}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}

/** The voice note takes its records as `VoiceLink`s (the stop page's recorder, §11.2). */
function voiceLinks(prefill: NewPrefill): VoiceLink[] {
  return (prefill.links ?? []).flatMap((link): VoiceLink[] => {
    if (link.type === 'contact') return [{ contactId: link.id, label: link.label }]
    if (link.type === 'property') return [{ propertyId: link.id, label: link.label }]
    if (link.type === 'job') return [{ jobId: link.id, label: link.label }]
    return []
  })
}

export function NewSheets({ kind, prefill, onClose, canText = true, canRequest = false }: NewSheetsProps) {
  const phone = usePhone()
  const { me, reloadBadge } = useActivity()
  /* The phone's menu swaps itself for the sheet picked; everything else is the kind asked for. */
  const [shown, setShown] = useState<NewSheetKind | null>(kind)
  const [start, setStart] = useState<NewPrefill>(() => readPrefill(prefill))
  /* A new ask (another kind, another prefill) starts again; the same one leaves a sheet being typed in alone. */
  useEffect(() => {
    setShown(kind)
    setStart(readPrefill(prefill))
  }, [kind, prefill])

  /* *Move* on a task is the Task sheet's (§8.2), not a new one. */
  useEffect(() => {
    if (shown === 'task' && start.editId) {
      openTaskSheet(start.editId)
      onClose()
    }
  }, [shown, start.editId, onClose])

  if (!shown) return null
  const made = () => {
    reloadBadge()
    window.dispatchEvent(new Event(ACTIVITY_MADE_EVENT))
  }
  const close = () => { setShown(null); onClose() }

  switch (shown) {
    case 'menu':
      return (
        <NewMenu canText={canText} canRequest={canRequest} forName={me.user.name} onClose={close}
          onPick={(picked) => {
            if (picked === 'request') { close(); openNewRequest(); return }
            setShown(picked)
          }} />
      )
    case 'text':
      return (
        <TextSheet prefill={start} phone={phone} onClose={close} onMade={made}
          onSwitch={(next, from) => { setStart(from); setShown(next) }} />
      )
    case 'email':
      return <EmailSheet prefill={start} phone={phone} onClose={close} onMade={made} />
    case 'task':
      return start.editId ? null : <TaskSheet prefill={start} phone={phone} onClose={close} onMade={made} />
    case 'meeting':
      return <MeetingSheet prefill={start} phone={phone} onClose={close} onMade={made} />
    case 'note':
      return <NoteSheet prefill={start} phone={phone} onClose={close} onMade={made} />
    case 'voice':
      return (
        <VoiceNoteSheet links={voiceLinks(start)} madeFromActivityId={start.fromActivityId}
          onClose={close} onSaved={made} />
      )
    default:
      return null
  }
}
